// Function to format the date into weekday-month-day format
export const formatTimeCardDate = (date: string) => {
  // Input: A string representing a date in any valid format (e.g., "2025-04-25T12:00:00")
  // Output: A formatted string in the "weekday-month-day" format (e.g., "Mon-Apr-25")
  const d = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short", // Day of the week, e.g. Mon, Tue
    // year: "numeric",
    month: "short", // e.g. Apr
    day: "numeric",
  };
  return d.toLocaleDateString("en-US", options);
};
export const formatTimeCardTime = (timeString: string) => {
  // Input: A string representing time in ISO format (e.g., "2025-04-25T12:00:00")
  // Output: A formatted string representing time in "hh:mm AM/PM" format (e.g., "12:00 PM")
  const date = new Date(timeString);
  if (isNaN(date.getTime())) {
    return "⚠️"; // Invalid date, return empty string
  }
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const convertDecimalHoursToHM = (hours: number): string => {

  const wholeHours = Math.floor(hours); // Get the whole hours part
  const decimalPart = hours - wholeHours; // Get the decimal part
  const minutes = Math.round(decimalPart * 60); // Convert the decimal part into minutes

  return `${wholeHours}h ${minutes}m`; // Return the formatted string
};

export function formatDateTime12hour(input: string): string {
  // Input: A string representing datetime in format "YYYY-MM-DD HH:mm:ss" (e.g., "2025-04-25 15:00:00")
  // Output: A string representing the datetime in 12-hour format (e.g., "Apr 25, 2025, 03:00 PM")
  if (!input || isNaN(new Date(input).getTime())) {
    return ""; 
  }
  const date = new Date(input.replace(" ", "T")); // Convert to ISO format
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  // Format using locale string
  return date
    .toLocaleString("en-GB", options)
    .replace(",", "") // Remove comma after date
    .replace(/(\d{2}):(\d{2})/, (match, h, m) => {
      // Ensure 12-hour format with lowercase am/pm
      const hour = date.getHours();
      const suffix = hour >= 12 ? "pm" : "am";
      const hour12 = (((hour + 11) % 12) + 1).toString().padStart(2, "0");
      return `${hour12}:${m}`;
    });
}

// Get the current date at midnight
export const getTodayAtMidnight = (): string => {
  // Output: A string representing today's date at midnight (next day) in "YYYY-MM-DD" format (e.g., "2025-04-26")
  const today = new Date();
  today.setDate(today.getDate() + 1);
  today.setHours(0, 0, 0, 0); // Set time to 00:00:00
  return today.toISOString().split("T")[0]; // Return in YYYY-MM-DD format
};

// Get tomorrow's date at midnight
export const getTomorrowAtMidnight = (): string => {
  // Output: A string representing tomorrow's date at midnight (next day) in "YYYY-MM-DD" format (e.g., "2025-04-27")
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 2); // Move to the next day
  tomorrow.setHours(0, 0, 0, 0); // Set time to 00:00:00
  return tomorrow.toISOString().split("T")[0]; // Return in YYYY-MM-DD format
};

// Get the start of this week (Monday) and end of this week (Sunday)
export const getStartAndEndOfWeek = (): { start: string; end: string } => {
  // Output: An object with "start" (Monday) and "end" (Sunday) of the current week in "YYYY-MM-DD" format
  const today = new Date();
  const dayOfWeek = today.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Get difference to Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0); // Start of the week (Monday at midnight)

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999); // End of the week (Sunday at midnight)

  return {
    start: monday.toISOString().split("T")[0], // Start of the week (Monday)
    end: sunday.toISOString().split("T")[0], // End of the week (Sunday)
  };
};

export const convertToDateTimeLocalFormat = (datetime: string) => {
  // Input: A string representing datetime in format "YYYY-MM-DD HH:mm:ss" (e.g., "2025-04-25 15:00:00")
  // Output: A string formatted as "YYYY-MM-DDTHH:mm" for use with <input type="datetime-local">
  if (!datetime || datetime=== null) return "";
  const [date, time] = datetime.split(" ");
  const [hours, minutes] = time.split(":");
  const formattedDateTime = `${date}T${hours}:${minutes}`;
  return formattedDateTime;
};

export const convertToFullDateTime = (datetime: string): string => {
  const date = new Date(datetime); // Convert the input string to a Date object
  //input "2025-04-15T11:20"
  // Output: "2025-04-15 11:20:00"
  // Format the date and time to the required format (YYYY-MM-DD HH:mm:ss)
  if (!datetime || isNaN(new Date(datetime).getTime())) {
    return ""; 
  }
  const formattedDate = date.toISOString().split("T")[0]; // Extract the date part (YYYY-MM-DD)
  const formattedTime = date.toTimeString().split(" ")[0]; // Extract the time part (HH:mm:ss)

  return `${formattedDate} ${formattedTime}`; // Return the combined formatted date and time
};


export   function getCurrentTime() {
    const now = new Date();
    
    // Format components with leading zeros
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    // Construct the formatted string
    const formattedTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    return formattedTime;
}

export function convertMinutesToHoursAndMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60); 
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes} m`;
}


export function formatDurationStartEndDate(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return ""; // invalid date inputs
  }

  let diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs <= 0) return "0min"; // no duration or negative

  const msInMinute = 60 * 1000;
  const msInHour = 60 * msInMinute;
  const msInDay = 24 * msInHour;

  const days = Math.floor(diffMs / msInDay);
  diffMs -= days * msInDay;

  const hours = Math.floor(diffMs / msInHour);
  diffMs -= hours * msInHour;

  const minutes = Math.floor(diffMs / msInMinute);

  let result = "";
  if (days > 0) result += `${days}d `;
  if (hours > 0) result += `${hours}hr `;
  if (minutes > 0) result += `${minutes}min`;

  return result.trim();
}
